import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Select, Switch, Row, Col, Upload, Button, Progress } from 'antd';
import { MdOutlineFileUpload, MdDeleteOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import { GUIDE_ICONS, GuideIcon } from './guideIcons';
import GuideDescriptionEditor from './GuideDescriptionEditor';
import GuidePhonePreview from './GuidePhonePreview';
import ImageCropModal from './ImageCropModal';
import { readFileAsDataURL } from './cropImage';
import styles from './styles.module.css';

// Still photos are cropped, resized and compressed in the browser before upload,
// so the source can be large. Animated GIFs and MP4 videos can't be re-encoded in
// the browser, so they're uploaded as-is (no crop) up to the server's limits.
const MAX_SOURCE_MB = 20;
const MAX_GIF_MB = 5;
const MAX_VIDEO_MB = 30;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const VIDEO_TYPES = ['video/mp4'];
const ACCEPTED_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES];

const formatKB = (bytes) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * position / total: where this slide sits in the guide, for the preview's
 * step counter and dots (new pages are appended at the end).
 */
const UserGuidePageForm = ({ visible, page, onClose, onSuccess, position = 1, total = 1 }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(0);
    // What the preview shows: presigned S3 link, or an external URL from older pages
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewType, setPreviewType] = useState('image'); // 'image' | 'video'
    // Image waiting in the crop dialog: { src: dataURL, name, size }
    const [cropSource, setCropSource] = useState(null);
    // S3 keys uploaded while this form is open; unsaved ones are deleted on close
    const sessionUploads = useRef([]);
    const isEdit = !!page;

    // Live values for the phone preview
    const title = Form.useWatch('title', form);
    const body = Form.useWatch('body', form);
    const icon = Form.useWatch('icon', form);

    useEffect(() => {
        if (!visible) return;
        sessionUploads.current = [];
        if (page) {
            form.setFieldsValue({
                title: page.title,
                body: page.body,
                icon: page.icon || 'info',
                imageKey: page.imageKey || null,
                // For S3 images imageUrl is a temporary presigned link — never save it back
                imageUrl: page.imageKey ? null : (page.imageUrl || null),
                isActive: page.isActive,
            });
            setPreviewUrl(page.imageUrl || null);
            setPreviewType(page.mediaType || 'image');
        } else {
            form.resetFields();
            form.setFieldsValue({ icon: 'info', isActive: true, imageKey: null, imageUrl: null });
            setPreviewUrl(null);
            setPreviewType('image');
        }
    }, [visible, page, form]);

    const uploadFile = async (file, originalSize = file.size) => {
        setIsUploading(true);
        setUploadPercent(0);
        try {
            const res = await BreathAPIs.uploadUserGuideImage(file, setUploadPercent);
            const { key, url, size, mediaType = 'image' } = res.data.data;
            // The file being replaced was only uploaded in this form → delete it now
            discardIfUnsaved(form.getFieldValue('imageKey'));
            sessionUploads.current.push(key);
            form.setFieldsValue({ imageKey: key, imageUrl: null });
            setPreviewUrl(url);
            setPreviewType(mediaType);
            if (mediaType === 'video') {
                toast.success(`Video uploaded (${formatKB(size)}).`);
            } else if (size && size < originalSize) {
                toast.success(`Image uploaded and optimized: ${formatKB(originalSize)} → ${formatKB(size)}`);
            } else {
                toast.success('Image uploaded.');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error uploading file.');
        } finally {
            setIsUploading(false);
        }
    };

    // Validates the picked file, then opens the cropper (GIFs and videos upload directly).
    // Always returns false so antd doesn't auto-upload.
    const beforeUpload = (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error('Only PNG, JPG, WEBP, GIF images or MP4 videos are allowed.');
            return Upload.LIST_IGNORE;
        }
        const video = VIDEO_TYPES.includes(file.type);
        const animated = file.type === 'image/gif';
        const maxMb = video ? MAX_VIDEO_MB : animated ? MAX_GIF_MB : MAX_SOURCE_MB;
        if (file.size > maxMb * 1024 * 1024) {
            toast.error(`${video ? 'Videos' : animated ? 'GIFs' : 'Images'} must be ${maxMb} MB or smaller.`);
            return Upload.LIST_IGNORE;
        }
        if (video || animated) {
            uploadFile(file);
            return false;
        }
        readFileAsDataURL(file)
            .then((src) => setCropSource({ src, name: file.name, size: file.size }))
            .catch(() => toast.error('Could not read this image.'));
        return false;
    };

    const handleCropped = (croppedFile) => {
        const originalSize = cropSource?.size;
        setCropSource(null);
        uploadFile(croppedFile, originalSize);
    };

    // Deletes `key` right away if it was uploaded in this form session (so no page
    // can be using it). The page's saved image is left alone until Save.
    const discardIfUnsaved = (key) => {
        if (!key || !sessionUploads.current.includes(key)) return;
        sessionUploads.current = sessionUploads.current.filter((k) => k !== key);
        BreathAPIs.discardUserGuideImages([key]).catch(() => {});
    };

    const removeImage = () => {
        discardIfUnsaved(form.getFieldValue('imageKey'));
        form.setFieldsValue({ imageKey: null, imageUrl: null });
        setPreviewUrl(null);
        setPreviewType('image');
    };

    // Deletes this session's uploads that no page uses (the server skips any
    // key that is saved on a page, e.g. the one just saved). Fire-and-forget.
    const discardUnsavedUploads = () => {
        const keys = sessionUploads.current;
        sessionUploads.current = [];
        if (keys.length) {
            BreathAPIs.discardUserGuideImages(keys).catch(() => {});
        }
    };

    const handleCancel = () => {
        setCropSource(null);
        discardUnsavedUploads();
        onClose();
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setIsLoading(true);
        try {
            const payload = {
                ...values,
                imageKey: values.imageKey || null,
                imageUrl: values.imageKey ? null : (values.imageUrl || null),
            };
            if (isEdit) {
                await BreathAPIs.updateUserGuidePage(page._id, payload);
                toast.success('Guide page updated.');
            } else {
                await BreathAPIs.createUserGuidePage(payload);
                toast.success('Guide page created.');
            }
            discardUnsavedUploads();
            onSuccess();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error saving guide page.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            title={isEdit ? 'Edit Guide Page' : 'New Guide Page'}
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            okText={isEdit ? 'Save' : 'Create'}
            confirmLoading={isLoading}
            okButtonProps={{ disabled: isUploading }}
            width={820}
            destroyOnClose
        >
            <Row gutter={24}>
                <Col xs={24} md={14}>
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[{ required: true, message: 'Title is required' }]}
                        >
                            <Input maxLength={40} showCount placeholder="e.g. Find a charger" />
                        </Form.Item>
                        <Form.Item
                            name="body"
                            label="Description"
                            extra="Select text and use the toolbar to make it bold or a bullet list."
                            rules={[{ required: true, message: 'Description is required' }]}
                        >
                            <GuideDescriptionEditor
                                rows={6}
                                maxLength={500}
                                placeholder={'Short intro sentence.\n- First point\n- Second point'}
                            />
                        </Form.Item>
                        <Form.Item name="icon" label="Icon">
                            <Select
                                options={Object.entries(GUIDE_ICONS).map(([key, { label }]) => ({
                                    value: key,
                                    label: (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            <GuideIcon name={key} size={18} /> {label}
                                        </span>
                                    ),
                                }))}
                            />
                        </Form.Item>

                        {/* Image is stored in S3; these hidden fields carry the saved reference */}
                        <Form.Item name="imageKey" hidden><Input /></Form.Item>
                        <Form.Item name="imageUrl" hidden><Input /></Form.Item>

                        <Form.Item
                            label="Image or video (optional)"
                            extra={
                                isEdit && page?.imageKey
                                    ? `Replacing or removing takes effect when you click Save — the previous file is then deleted from storage.`
                                    : `Shown instead of the icon. PNG, JPG or WEBP up to ${MAX_SOURCE_MB} MB (you can crop it next), animated GIF up to ${MAX_GIF_MB} MB, or MP4 video (H.264) up to ${MAX_VIDEO_MB} MB. GIFs and videos are uploaded as-is; videos play muted and on a loop.`
                            }
                        >
                            {isUploading && (
                                <Progress percent={uploadPercent} size="small" status="active" style={{ marginBottom: 8 }} />
                            )}
                            {previewUrl ? (
                                <div className={styles['image-field']}>
                                    {previewType === 'video' ? (
                                        <video src={previewUrl} className={styles['image-thumb']} muted loop autoPlay playsInline />
                                    ) : (
                                        <img src={previewUrl} alt="" className={styles['image-thumb']} />
                                    )}
                                    <div className={styles['image-actions']}>
                                        <Upload
                                            accept={ACCEPTED_TYPES.join(',')}
                                            showUploadList={false}
                                            beforeUpload={beforeUpload}
                                            customRequest={() => {}}
                                        >
                                            <Button icon={<MdOutlineFileUpload />} loading={isUploading}>
                                                Replace
                                            </Button>
                                        </Upload>
                                        <Button danger icon={<MdDeleteOutline />} onClick={removeImage} disabled={isUploading}>
                                            Remove
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Upload.Dragger
                                    accept={ACCEPTED_TYPES.join(',')}
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                    customRequest={() => {}}
                                    disabled={isUploading}
                                >
                                    <p style={{ margin: 0, fontSize: 28, color: '#1677ff' }}>
                                        <MdOutlineFileUpload />
                                    </p>
                                    <p style={{ margin: '4px 0 0' }}>
                                        {isUploading ? 'Uploading…' : 'Click or drag an image or MP4 video here to upload'}
                                    </p>
                                </Upload.Dragger>
                            )}
                        </Form.Item>

                        <Form.Item name="isActive" label="Visible in app" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </Col>
                <Col xs={24} md={10}>
                    <GuidePhonePreview
                        title={title}
                        body={body}
                        icon={icon}
                        imageUrl={previewUrl}
                        mediaType={previewType}
                        position={position}
                        total={total}
                    />
                </Col>
            </Row>

            <ImageCropModal
                source={cropSource}
                onCancel={() => setCropSource(null)}
                onDone={handleCropped}
            />
        </Modal>
    );
};

export default UserGuidePageForm;
